import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FolderKanban,
  BarChart3,
  Mail,
  Bot,
  Mic as MicIcon,
  PencilLine,
  Sun,
  Moon,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/cn';

const sections = [
  {
    to: '/settings/projects',
    title: 'Projects',
    description: 'Edit the project registry: name, description, aliases, key entities.',
    icon: FolderKanban,
    available: true,
  },
  {
    to: '/settings/usage',
    title: 'Token usage',
    description: 'Daily AI cost by model and prompt. Cache hit rate. Top expensive calls.',
    icon: BarChart3,
    available: true,
  },
  {
    to: '/settings/accounts',
    title: 'Email accounts',
    description: 'Connect Gmail accounts via OAuth.',
    icon: Mail,
    available: true,
  },
  {
    to: '/settings/telegram',
    title: 'Telegram',
    description: 'Bot status, allowed chat id, webhook.',
    icon: Bot,
    available: false,
    phase: 'Phase 5',
  },
  {
    to: '/settings/voice',
    title: 'Voice',
    description: 'Whisper provider, audio retention window.',
    icon: MicIcon,
    available: false,
    phase: 'Phase 6',
  },
  {
    to: '/settings/writing-style',
    title: 'Writing style',
    description: 'Few-shot examples added when drafts nail (or miss) your voice.',
    icon: PencilLine,
    available: false,
    phase: 'Phase 4',
  },
];

export function SettingsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Configure projects, integrations, and the system itself.
        </p>
      </div>
      <AppearanceCard />
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
        className="grid grid-cols-1 gap-3 md:grid-cols-2"
      >
        {sections.map((s) => {
          const Icon = s.icon;
          const content = (
            <Card
              className={
                s.available
                  ? 'h-full transition-colors hover:border-foreground/20'
                  : 'h-full opacity-60'
              }
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-md bg-muted">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium leading-tight">{s.title}</p>
                      {!s.available && <Badge variant="outline">{s.phase}</Badge>}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{s.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          return (
            <motion.div
              key={s.to}
              variants={{
                hidden: { opacity: 0, y: 6 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { type: 'spring', stiffness: 120, damping: 14 },
                },
              }}
              whileHover={s.available ? { y: -2 } : undefined}
            >
              {s.available ? <Link to={s.to}>{content}</Link> : content}
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}

function AppearanceCard() {
  const { theme, setTheme } = useTheme();
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div>
          <p className="font-medium leading-tight">Appearance</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Light is default. Dark stays available; choice persists across sessions.
          </p>
        </div>
        <div className="inline-flex rounded-md border border-border bg-background p-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTheme('light')}
            className={cn(
              'h-7 gap-1 rounded px-2.5 text-xs',
              theme === 'light' ? 'bg-accent text-foreground' : 'text-muted-foreground',
            )}
          >
            <Sun className="h-3.5 w-3.5" /> Light
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTheme('dark')}
            className={cn(
              'h-7 gap-1 rounded px-2.5 text-xs',
              theme === 'dark' ? 'bg-accent text-foreground' : 'text-muted-foreground',
            )}
          >
            <Moon className="h-3.5 w-3.5" /> Dark
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
